import { isEmpty, isNil } from 'lodash';
import { environment } from '@env/environment';
import { EgAlarmEvent } from '@app/energy-guard/models/alarms';
import { consumerKomKeys, producerKomKeys } from '@app/shared/constants';
import { MeterViewFilterOption } from './meter-operator-filter';
import { StringUtility } from '@app/shared/string-utility';
import { InstallationTypes } from '@app/shared/enums';
import { BuildingTypes } from '@app/shared/models/building-types';
import { EAProperties } from '@app/efficiency-analysis/models/efficiency-analysis-properties';
import { NullableDate } from '@app/shared/models/nullable';
import { MeterAlarm } from '../../../energy-guard/models/alarms';

export class MeterView extends EAProperties {
  alarms?: EgAlarmEvent[];
  billingCategory!: string;
  buildingId!: string;
  buildingTypesName?: string;
  checked?: boolean;
  connectionObjectNumber!: string;
  contractAccount!: string;
  converterFactor!: number;
  description!: string;
  deviceEUI!: string;
  deviceId!: string;
  direction!: string;
  district!: string;
  energyTypesName!: string;
  filterOptions?: MeterViewFilterOption[];
  guid!: string;
  houseNumber!: string | null;
  hoverMeterTypesName?: string;
  installationType!: string;
  isEnergyGuardLicenseAvailable!: boolean;
  isIOTFeedOnly!: boolean;
  komKey!: string;
  komKeyArray?: string[];
  marketLocationKey!: string;
  measurementLocationKey!: string;
  meterNumber!: string;
  meterTypeCode!: string;
  meterTypesName!: string;
  msbid!: string;
  municipality!: string;
  municipalityId!: string;
  municipalityKey!: any;
  notes!: string;
  nummers?: string[] = [];
  obisId?: string;
  objectType!: string;
  objectTypeObj?: BuildingTypes;
  sensorID?: string;
  serialNumber!: string;
  street!: string;
  timeBoundAlarms?: EgAlarmEvent[] = [];
  usageEndDate: NullableDate = null;
  usageStartDate!: Date;
  wholeDayAlarms?: EgAlarmEvent[] = [];
  zipCode!: string;
  meterAlarm?: MeterAlarm;
  isNoteUpdateSuccess!: boolean;

  getAddress(sanitize: boolean = false): string {
    let address = this.street?.concat(' ', this.houseNumber || '', '\n\r', this.zipCode || '', ' ', this.district || '');
    if (sanitize) {
      address = address.replace('\n\r', ' ').trim();
    }
    return address;
  }

  getMeterAddress(sanitize: boolean = false): string {
    const street = this.street || '';
    const houseNumber = this.houseNumber || '';
    const zipCode = this.zipCode || '';
    const district = this.district || '';
    const municipality = this.municipality;

    const addressLine1 = [street, houseNumber].filter(Boolean).join(' ');
    const addressLine2 = municipality ? `${zipCode} ${municipality}, ${district}` : `${zipCode} ${district}`;

    return sanitize ? `${addressLine1} ${addressLine2}` : `${addressLine1}\n\r${addressLine2}`;
  }

  getMeterNumberWithOutLeadingZeros(): string {
    return this.meterNumber.replace(/^0+/, '');
  }
  getSensorNumberWithOutLeadingZeros(): string | null {
    return this.deviceEUI ? this.deviceEUI.replace(/^0+/, '') : null;
  }
  getMarketLocationKeyWithOutLeadingZeros(): string | null {
    return this.marketLocationKey ? this.marketLocationKey.replace(/^0+/, '') : null;
  }

  private getGmsbOrWmsbMeterOperator(): MeterViewFilterOption | undefined {
    return this.filterOptions?.find((option: MeterViewFilterOption) => option.filterValue.includes(this.msbid));
  }

  private getNonGmsbOrWmsbMeterOperator(): MeterViewFilterOption {
    return this.filterOptions?.find((option: MeterViewFilterOption) => option.filterValue === 'others') as MeterViewFilterOption;
  }

  private determineMeterOperator(): MeterViewFilterOption {
    let meterOperator = this.getGmsbOrWmsbMeterOperator();
    if (isNil(meterOperator)) {
      meterOperator = this.getNonGmsbOrWmsbMeterOperator();
    }
    return meterOperator;
  }

  getMarketOperatorText(): string {
    let meterOperator = this.determineMeterOperator();
    return meterOperator?.filterName;
  }

  getMarketOperatorCategory(): string {
    let meterOperator = this.determineMeterOperator();
    return meterOperator?.msbCategory;
  }

  isProducer() {
    const noKomKey = environment.noKomKeyMunicipality.split(';');
    if (noKomKey.indexOf(this.municipalityKey) > -1 && StringUtility.areStringsIdentical(this.direction, InstallationTypes.Z06)) {
      return true;
    }
    const keys = [this.komKey];
    return StringUtility.hasAnyMatchInArray(producerKomKeys, keys);
  }

  isConsumer() {
    const noKomKey = environment.noKomKeyMunicipality.split(';');
    if (noKomKey.indexOf(this.municipalityKey) > -1 && StringUtility.areStringsIdentical(this.direction, InstallationTypes.Z07)) {
      return true;
    }
    const keys = [this.komKey];
    return StringUtility.hasAnyMatchInArray(consumerKomKeys, keys);
  }

  isBadgeVisible(): boolean {
    return !isNil(this.alarms) && !isEmpty(this.alarms);
  }
}

export interface MeterViewResponse {
  data: MeterView[];
  maxRowCount: number;
}
